import type { ScriptStep } from '@shared/interfaces/assistantScripts';
import type { PollyLanguage } from '@shared/interfaces/polly';

// Word-rate used to estimate step playback length when no audio metadata is
// available. ~133 wpm matches Polly Neural defaults closely enough for a
// preview indicator.
const SECONDS_PER_WORD = 0.45;

export const parseScriptSteps = (
  scriptData: string | undefined | null,
): ScriptStep[] => {
  if (!scriptData) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(scriptData);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (step): step is ScriptStep =>
        Boolean(step) && typeof step === 'object',
    );
  } catch {
    return [];
  }
};

export const serializeScriptSteps = (steps: ScriptStep[]): string =>
  JSON.stringify(steps);

const countWords = (text: string): number => {
  const trimmed = text.trim();
  if (!trimmed) {
    return 0;
  }
  return trimmed.split(/\s+/).length;
};

export const estimateStepSeconds = (text: string): number =>
  Math.max(1, Math.round(countWords(text) * SECONDS_PER_WORD));

export const formatSeconds = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '0:00';
  }
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
};

export const totalEstimatedSeconds = (steps: ScriptStep[]): number =>
  steps.reduce(
    (sum, step) =>
      sum + estimateStepSeconds(step.text ?? ''),
    0,
  );

// Returns true when every step in `steps` has a non-empty translation for
// `language`. Used by the translations bar to mark a language chip as
// "complete" (green check). The base language is always considered complete
// when at least one step's `text` is non-empty.
export const isLanguageComplete = (
  steps: ScriptStep[],
  language: PollyLanguage,
  baseLanguage: PollyLanguage,
): boolean => {
  if (steps.length === 0) {
    return false;
  }
  if (language === baseLanguage) {
    return steps.every((s) => (s.text ?? '').trim().length > 0);
  }
  return steps.every((s) => {
    const value = s.translations?.[language];
    return typeof value === 'string' && value.trim().length > 0;
  });
};

// Returns the languages that have at least one missing translation step, so
// the "Translate missing…" link can target only those.
export const findMissingLanguages = (
  steps: ScriptStep[],
  languages: PollyLanguage[],
  baseLanguage: PollyLanguage,
): PollyLanguage[] =>
  languages.filter(
    (lang) =>
      lang !== baseLanguage && !isLanguageComplete(steps, lang, baseLanguage),
  );

export const hasQuiz = (step: ScriptStep): boolean =>
  Boolean(step.questionary && step.questionary.type !== 'none');
